/* The API client, adapted from apps/admin/src/data/api.ts. Read the comments
 * there: every rule below (relative paths, one refresh in flight, the cross-tab
 * lock, offline is not signed out) was learned the hard way in the console.
 *
 * What differs here: the session key and lock name are the agency app's own, so
 * a person signed in to the console in another tab is not signed out of either
 * one; sign-in is a PASSWORD sign-in with app_variant AGENCY_DESKTOP (the
 * server never issues an agency session from an OTP); and the OTP calls return
 * a throwaway token for the registration flow instead of storing a session. */

const STORAGE_KEY = 'jd.agency.session';
const LOCK_NAME = 'jd.agency.refresh';

interface StoredSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  email: string;
}

function load(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredSession;
    if (!s.accessToken || !s.refreshToken) return null;
    return s;
  } catch {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* storage blocked: nothing to clear */
    }
    return null;
  }
}

let session: StoredSession | null = load();
const listeners = new Set<() => void>();

function setSession(next: StoredSession | null) {
  session = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage blocked: the session lives in memory for this tab only */
  }
  listeners.forEach((l) => l());
}

export function onSessionChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function hasSession(): boolean {
  return session !== null;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== STORAGE_KEY && e.key !== null) return;
    session = load();
    listeners.forEach((l) => l());
  });
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: Record<string, unknown>;
  constructor(status: number, code: string, message: string, body: Record<string, unknown> = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

async function readBody(res: Response): Promise<Record<string, unknown>> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function errorOf(res: Response, body: Record<string, unknown>, fallbackCode: string, fallbackMessage: string): ApiError {
  return new ApiError(
    res.status,
    typeof body.code === 'string' ? body.code : fallbackCode,
    typeof body.message === 'string' ? body.message : fallbackMessage,
    body,
  );
}

/* ------------------------------------------------------------------ sign in */

/** Email + password sign-in to the agency app (000082: no mobile number, no
 *  OTP). The server accepts AGENCY_DESKTOP only for a live member of an agency
 *  (NO_AGENCY_MEMBERSHIP otherwise). */
export async function signIn(email: string, password: string): Promise<void> {
  const res = await fetch('/auth/password/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identifier: email, password, app_variant: 'AGENCY_DESKTOP' }),
  });
  const body = await readBody(res);
  if (!res.ok) throw errorOf(res, body, 'SIGN_IN_FAILED', 'Could not sign in.');
  storeTokens(body as unknown as TokenResponse, email);
}

function storeTokens(t: TokenResponse, email: string) {
  setSession({
    accessToken: t.access_token,
    refreshToken: t.refresh_token,
    expiresAt: Date.now() + t.expires_in * 1000,
    email,
  });
}

/** "Forgot password?" (000094): POST /auth/password/forgot emails a link to
 *  set a new one. The answer is the same whether or not the email has an
 *  account; 503 EMAIL_NOT_CONFIGURED when the server cannot send email yet. */
export async function forgotPassword(email: string): Promise<string> {
  const res = await fetch('/auth/password/forgot', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, app_variant: 'AGENCY_DESKTOP' }),
  });
  const body = await readBody(res);
  if (!res.ok) throw errorOf(res, body, 'FORGOT_FAILED', 'Could not send the link.');
  return typeof body.message === 'string' ? body.message : 'If that email has an account, a link is on its way.';
}

export type ResetMode = 'direct' | 'email' | 'off';

/** What "Forgot password?" does on this server: GET /auth/password/reset-mode. */
export async function getResetMode(): Promise<ResetMode> {
  const res = await fetch('/auth/password/reset-mode');
  const body = await readBody(res);
  const mode = (body.data as { mode?: ResetMode } | undefined)?.mode;
  return mode ?? 'off';
}

/** For now (owner, 2026-10-29): the email and a new password, set at once —
 *  POST /auth/password/reset-direct. Until email links are switched on. */
export async function directResetPassword(email: string, password: string): Promise<string> {
  const res = await fetch('/auth/password/reset-direct', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, app_variant: 'AGENCY_DESKTOP' }),
  });
  const body = await readBody(res);
  if (!res.ok) throw errorOf(res, body, 'RESET_FAILED', 'Could not set the password.');
  return typeof body.message === 'string' ? body.message : 'Your password is changed.';
}

/** The link's page: POST /auth/password/reset with its token and the new password. */
export async function resetPassword(token: string, password: string): Promise<string> {
  const res = await fetch('/auth/password/reset', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, password }),
  });
  const body = await readBody(res);
  if (!res.ok) throw errorOf(res, body, 'RESET_FAILED', 'Could not set the password.');
  return typeof body.message === 'string' ? body.message : 'Your password is changed.';
}

export function signOut(): void {
  void fetch('/auth/logout', {
    method: 'POST',
    headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
  }).catch(() => undefined);
  setSession(null);
}

/* ------------------------------------------------------------------ registration */

export interface AgencySignup {
  full_name: string;
  email: string;
  password: string;
  legal_name: string;
  display_name: string;
  contact_phone_e164: string;
  contact_email: string;
  city_code: string;
  registered_address_line_1: string;
  registered_address_line_2: string;
  registered_pincode: string;
}

/** "Register your agency" in one call (POST /agencies/signup): the account,
 *  the agency at PENDING_REVIEW with this person as OWNER, and a signed-in
 *  session — the same tokens a sign-in returns. */
export async function signUpAgency(d: AgencySignup): Promise<void> {
  const res = await fetch('/agencies/signup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(d),
  });
  const body = await readBody(res);
  if (!res.ok) throw errorOf(res, body, 'SIGN_UP_FAILED', 'Could not register the agency.');
  storeTokens(body as unknown as TokenResponse, d.email);
}

/* ------------------------------------------------------------------ refresh */

type RefreshOutcome = 'ok' | 'dead' | 'offline';
let refreshing: Promise<RefreshOutcome> | null = null;

function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? (locks.request(LOCK_NAME, fn) as Promise<T>) : fn();
}

async function refresh(): Promise<RefreshOutcome> {
  if (!session) return 'dead';
  if (refreshing) return refreshing;
  const failed = session.refreshToken;

  refreshing = withRefreshLock(async (): Promise<RefreshOutcome> => {
    try {
      const latest = load();
      if (!latest) {
        setSession(null);
        return 'dead';
      }
      // Another tab already rotated: adopt its token, never rotate a retired one.
      if (latest.refreshToken !== failed) {
        session = latest;
        listeners.forEach((l) => l());
        return 'ok';
      }
      const res = await fetch('/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: latest.refreshToken }),
      });
      if (!res.ok) {
        setSession(null);
        return 'dead';
      }
      const body = (await res.json()) as TokenResponse;
      if (!session) return 'dead';
      setSession({
        ...latest,
        accessToken: body.access_token,
        refreshToken: body.refresh_token,
        expiresAt: Date.now() + body.expires_in * 1000,
      });
      return 'ok';
    } catch {
      return 'offline';
    } finally {
      refreshing = null;
    }
  });
  return refreshing;
}

/* ------------------------------------------------------------------ fetch */

/** One API call, unwrapping `{data: …}`. Two refresh-and-retry attempts on a 401
 *  (see the console's api() for why two). A FormData body goes as multipart: the
 *  browser sets the boundary, so no Content-Type is set for it. */
export async function api<T>(path: string, init: RequestInit = {}, attemptsLeft = 2): Promise<T> {
  const headers = new Headers(init.headers);
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isForm && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (session) headers.set('Authorization', `Bearer ${session.accessToken}`);

  let res: Response;
  try {
    res = await fetch(path, { ...init, headers });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Could not reach the server. Check the connection and try again.');
  }

  if (res.status === 401 && attemptsLeft > 0) {
    const outcome = await refresh();
    if (outcome === 'ok') return api<T>(path, init, attemptsLeft - 1);
    if (outcome === 'dead') {
      setSession(null);
      throw new ApiError(401, 'SESSION_EXPIRED', 'Your session has expired. Sign in again.');
    }
    throw new ApiError(0, 'NETWORK', 'Could not reach the server. You have not been signed out.');
  }

  const body = await readBody(res);
  if (res.status === 403 && body.code === 'SESSION_REVOKED') {
    setSession(null);
    throw new ApiError(403, 'SESSION_REVOKED', 'You were signed out. Sign in again.');
  }
  if (!res.ok) throw errorOf(res, body, 'REQUEST_FAILED', 'The request failed.');
  return (body.data ?? body) as T;
}

/** A file the server sends (the price sheet, 2026-10-30), with the session —
 *  refreshed once on a 401, as api() does. The error is the server's words. */
export async function fetchFile(path: string, attemptsLeft = 1): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(path, { headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {} });
  } catch {
    throw new ApiError(0, 'NETWORK', 'Could not reach the server. Check the connection and try again.');
  }
  if (res.status === 401 && attemptsLeft > 0) {
    if ((await refresh()) === 'ok') return fetchFile(path, attemptsLeft - 1);
    throw new ApiError(401, 'SESSION_EXPIRED', 'Your session has expired. Sign in again.');
  }
  if (!res.ok) throw errorOf(res, await readBody(res), 'REQUEST_FAILED', 'The file could not be downloaded.');
  return res.blob();
}
